(function eventTracking() {
  /**
   * Determines the page template type based on the current URL path.
   * @returns {string|null} The page template type ('page', 'product', 'collection'), or null if not matched.
   */
  function getPageTemplate() {
    const path = window.location.pathname;

    if (path.includes('/pages/')) {
      return 'page';
    } else if (path.includes('/products/')) {
      return 'product';
    } else if (path.includes('/collections/')) {
      return 'collection';
    }

    return null;
  }

  /**
   * Retrieves the shipping country from the Shopify object.
   * @returns {string|null} The shipping country code, or null if not available.
   */
  function getShippingCountry() {
    if (window.Shopify && window.Shopify.country) {
      return window.Shopify.country.toLowerCase();
    }

    return null;
  }

  /**
   * Retrieves the clean page path by removing trailing slashes and locale/country subfolders.
   * @returns {string} The clean page path without locale/country prefix.
   */
  function getPagePathClean() {
    let path = window.location.pathname;

    path = path.replace(/^\/[a-z]{2}(-[a-z]{2,4})?\//, '/');
    path = path.replace(/\/$/, '');

    return path;
  }

  /**
   * Retrieves the theme version from the Shopify object.
   * @returns {string} The theme version, or an empty string if not available.
   */
  function getThemeVersion() {
    const version = window.Shopify?.theme?.schema_version || '';

    return version;
  }

  const pageTemplate = getPageTemplate();
  const pagePathClean = getPagePathClean();
  const shippingCountry = getShippingCountry();
  const themeVersion = getThemeVersion();

  (function eventTrackingBreadcrumbs() {
    const element = document.querySelector('section.section-breadcrumbs');

    if (!element) return;

    element.addEventListener('click', (e) => {
      const link = e.target instanceof Element && e.target.closest('.breadcrumbs-item a');
      const deadLink = e.target instanceof Element && e.target.closest('.breadcrumbs-item:not(:has(a))');

      if (link) {
        window.gtag('event', 'module_interaction', {
          interaction_type: 'click',
          module_type: 'breadcrumb',
          element_type: 'breadcrumb_item',
          element_text: link.textContent?.trim().toLowerCase() || '',
          element_index:
            Array.from(element.querySelectorAll('.breadcrumbs-item')).indexOf(
              link.closest('.breadcrumbs-item') || link,
            ) + 1,
          element_index_count: element.querySelectorAll('.breadcrumbs-item').length,
          page_template: pageTemplate,
          page_path_clean: pagePathClean,
          ship_to_country: shippingCountry,
          theme_version: themeVersion,
        });
      }

      if (deadLink) {
        window.gtag('event', 'module_interaction', {
          interaction_type: 'dead_click',
          module_type: 'breadcrumb',
          element_type: 'breadcrumb_item',
          element_text: deadLink.textContent?.trim().toLowerCase() || '',
          element_index:
            Array.from(element.querySelectorAll('.breadcrumbs-item')).indexOf(
              deadLink.closest('.breadcrumbs-item') || deadLink,
            ) + 1,
          element_index_count: element.querySelectorAll('.breadcrumbs-item').length,
          page_template: pageTemplate,
          page_path_clean: pagePathClean,
          ship_to_country: shippingCountry,
          theme_version: themeVersion,
        });
      }
    });

    /** @type {number|null} */
    let timer = null;

    new IntersectionObserver((entries, observer) => {
      if (entries.some((entry) => entry.isIntersecting)) {
        timer = Date.now();

        window.gtag('event', 'module_inview', {
          page_template: pageTemplate,
          module_type: 'breadcrumb',
          page_path_clean: pagePathClean,
          ship_to_country: shippingCountry,
          theme_version: themeVersion,
        });
      } else if (timer !== null) {
        const milliseconds = Date.now() - timer;

        timer = null;

        window.gtag('event', 'module_outview', {
          page_template: pageTemplate,
          module_type: 'breadcrumb',
          seconds_in_view: milliseconds / 1000,
          page_path_clean: pagePathClean,
          ship_to_country: shippingCountry,
          theme_version: themeVersion,
        });

        observer.unobserve(element);
      }
    }).observe(element);
  })();

  (function eventTrackingCollectionFilters() {
    const filterWrapper = document.querySelector('.modal-trap-container[data-focus-trap="filters-tray"]');

    if (!filterWrapper) return;

    let moduleInteractionCount = 0;

    /**
     * @param {CustomEvent<{ id?: string }>} e - The modal-trap-deactivate event.
     */
    function resetCountOnDeactivate(e) {
      if (e.detail?.id === 'filters-tray') moduleInteractionCount = 0;
    }

    window.addEventListener('modal-trap-deactivate', resetCountOnDeactivate);
    /** @type {HTMLInputElement | null} */
    let lastFilterOptionInput = null;
    /** @type {number} */
    let lastFilterOptionTime = 0;

    filterWrapper.addEventListener(
      'click',
      (e) => {
        const closeButton =
          e.target instanceof Element && e.target.closest('.icon-button[data-focus-trap-toggle="filters-tray"]');

        if (closeButton) {
          moduleInteractionCount += 1;
          window.gtag('event', 'filter_interaction', {
            interaction_type: 'close',
            module_type: 'filters_collection',
            element_type: 'icon',
            element_text: 'close',
            element_index: 1,
            element_index_count: 1,
            module_interaction_count: moduleInteractionCount,
            page_template: pageTemplate,
            page_path_clean: pagePathClean,
            ship_to_country: shippingCountry,
            theme_version: themeVersion,
          });
          moduleInteractionCount = 0;
          return;
        }

        const submitButton = e.target instanceof Element ? e.target.closest('.button-container button') : null;

        if (submitButton instanceof HTMLButtonElement) {
          const buttonContainer = submitButton.closest('.button-container');
          const buttons = buttonContainer?.querySelectorAll('button');
          const elementIndex = buttons ? Array.from(buttons).indexOf(submitButton) + 1 : undefined;
          const elementIndexCount = buttons?.length;
          const elementText = submitButton.textContent?.trim().toLowerCase() || '';

          moduleInteractionCount += 1;
          window.gtag('event', 'filter_interaction', {
            interaction_type: 'click',
            module_type: 'filters_collection',
            element_type: 'button_primary',
            element_text: elementText,
            element_index: elementIndex,
            element_index_count: elementIndexCount,
            module_interaction_count: moduleInteractionCount,
            page_template: pageTemplate,
            page_path_clean: pagePathClean,
            ship_to_country: shippingCountry,
            theme_version: themeVersion,
          });
          return;
        }

        const secondaryButton =
          e.target instanceof Element ? e.target.closest('.button-container .button--outline') : null;

        if (secondaryButton instanceof HTMLAnchorElement) {
          e.preventDefault();

          const buttonContainer = secondaryButton.closest('.button-container');
          const buttons = buttonContainer?.querySelectorAll('.button--outline');
          const elementIndex = buttons ? Array.from(buttons).indexOf(secondaryButton) + 1 : undefined;
          const elementIndexCount = buttons?.length;
          const elementText = secondaryButton.textContent?.trim().toLowerCase() || '';

          moduleInteractionCount += 1;
          window.gtag('event', 'filter_interaction', {
            interaction_type: 'click',
            module_type: 'filters_collection',
            element_type: 'button_secondary',
            element_text: elementText,
            element_index: elementIndex,
            element_index_count: elementIndexCount,
            module_interaction_count: moduleInteractionCount,
            page_template: pageTemplate,
            page_path_clean: pagePathClean,
            ship_to_country: shippingCountry,
            theme_version: themeVersion,
          });

          const href = secondaryButton.getAttribute('href');
          if (href) {
            setTimeout(() => {
              window.location.assign(href);
            }, 150);
          }
          return;
        }

        const optionItem = e.target instanceof Element ? e.target.closest('.filters-group-item') : null;
        const filterInput = optionItem?.querySelector('input');

        if (optionItem && filterInput instanceof HTMLInputElement) {
          const label = filterInput.closest('label') || document.querySelector(`label[for="${filterInput.id}"]`);
          const labelTextEl = label?.querySelector('.text-subtitle-md');
          const elementText = (labelTextEl?.textContent ?? label?.textContent)?.trim().toLowerCase() || '';
          const filterGroup = optionItem.closest('.filters-group');
          const groupItems = filterGroup?.querySelectorAll('.filters-group-item') ?? [];
          const elementIndex = groupItems.length ? Array.from(groupItems).indexOf(optionItem) + 1 : undefined;
          const elementIndexCount = groupItems.length || undefined;

          requestAnimationFrame(() => {
            if (filterInput === lastFilterOptionInput && Date.now() - lastFilterOptionTime < 100) return;
            lastFilterOptionInput = filterInput;
            lastFilterOptionTime = Date.now();

            const isNowChecked = filterInput.checked;
            moduleInteractionCount += 1;
            window.gtag('event', 'filter_interaction', {
              interaction_type: isNowChecked ? 'select' : 'deselect',
              module_type: 'filters_collection',
              element_type: 'filter_option',
              element_text: elementText,
              element_index: elementIndex,
              element_index_count: elementIndexCount,
              module_interaction_count: moduleInteractionCount,
              page_template: pageTemplate,
              page_path_clean: pagePathClean,
              ship_to_country: shippingCountry,
              theme_version: themeVersion,
            });
          });
          return;
        }

        const expandGroup = e.target instanceof Element && e.target.closest('.filters-item details');
        if (expandGroup) {
          const wasOpen = expandGroup.hasAttribute('open');
          const groupItem = expandGroup.closest('.filters-item');
          if (groupItem) {
            const filtersContainer = groupItem.closest('.filters-container');
            if (filtersContainer) {
              const groups = filtersContainer.querySelectorAll('.filters-item');
              const elementIndex = Array.from(groups).indexOf(groupItem) + 1;
              const elementIndexCount = groups.length;
              const summary = expandGroup.querySelector('summary');
              const elementText = summary?.textContent?.trim().toLowerCase() || '';

              moduleInteractionCount += 1;
              window.gtag('event', 'filter_interaction', {
                interaction_type: wasOpen ? 'collapse' : 'expand',
                module_type: 'filters_collection',
                element_type: 'filter_group',
                element_text: elementText,
                element_index: elementIndex,
                element_index_count: elementIndexCount,
                module_interaction_count: moduleInteractionCount,
                page_template: pageTemplate,
                page_path_clean: pagePathClean,
                ship_to_country: shippingCountry,
                theme_version: themeVersion,
              });
            }
          }
          return;
        }
      },
      true,
    );

    document.addEventListener('click', (e) => {
      const openButton =
        e.target instanceof Element ? e.target.closest('button[data-focus-trap-toggle="filters-tray"]') : null;

      if (!openButton) return;
      if (filterWrapper.contains(openButton)) return;

      moduleInteractionCount += 1;
      window.gtag('event', 'filter_interaction', {
        interaction_type: 'open',
        module_type: 'filters_collection',
        element_type: 'filters_toggle',
        element_text: 'filter + sort',
        element_index: 1,
        element_index_count: 1,
        module_interaction_count: moduleInteractionCount,
        page_template: pageTemplate,
        page_path_clean: pagePathClean,
        ship_to_country: shippingCountry,
        theme_version: themeVersion,
      });
    });
  })();
})();
